import { createClient } from '@supabase/supabase-js';
import { registerPwa } from './pwa.js';
import { isInstalledApp } from './install-state.js';
import { createSyncMetadata, decideInitialSync, hasMeaningfulBudgetData, validateBudgetData } from './sync-core.js';

registerPwa();

const config = window.__CLEARLEAF_CONFIG__ || {};
const DATA_KEY = 'clearleaf_data';
const META_KEY = 'clearleaf_sync_meta';
const OFFLINE_ACCESS_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
let supabase = null;
let currentUser = null;
let metadata = loadMetadata();
let syncTimer;
let syncInFlight = false;
let resyncRequested = false;
let statusNode = null;

if (!config.cloudEnabled) {
  revealApp();
} else {
  supabase = createClient(config.supabaseUrl, config.supabaseAnonKey, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true }
  });
  bootstrap().catch(error => showFatal(error));
}

async function bootstrap() {
  const callbackError = readAuthCallbackError();
  if (callbackError) {
    clearAuthCallbackError();
    showSignIn(callbackError);
    return;
  }
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw error;
  if (!session) {
    showSignIn();
    return;
  }
  if (!navigator.onLine) {
    if (isOfflineAccessValid(session.user)) {
      currentUser = session.user;
      revealAndStartSync({ reloadWhenOnline: true });
    } else {
      showOfflineVerificationRequired();
    }
    return;
  }
  try {
    await enterAuthenticatedApp(session.user);
  } catch (err) {
    // If it's a network/transport error (not an entitlement denial), fall back to offline
    if (isOfflineAccessValid(session.user) && err?.message && !/purchase not found|not active/i.test(err.message)) {
      currentUser = session.user;
      revealAndStartSync({ reloadWhenOnline: true });
    } else {
      throw err;
    }
  }
}

function readAuthCallbackError() {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const query = new URLSearchParams(window.location.search);
  const code = hash.get('error_code') || query.get('error_code');
  const description = hash.get('error_description') || query.get('error_description');
  const error = hash.get('error') || query.get('error');
  if (!code && !description && !error) return '';
  if (code === 'otp_expired') return 'That sign-in request expired or was already used. Enter your email to get a new code.';
  return description || 'That sign-in request did not work. Enter your email to get a new code.';
}

function clearAuthCallbackError() {
  const url = new URL(window.location.href);
  for (const name of ['error', 'error_code', 'error_description']) url.searchParams.delete(name);
  url.hash = '';
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}`);
}

async function enterAuthenticatedApp(user) {
  currentUser = user;
  const { error: claimError } = await supabase.rpc('claim_budget_tracker_entitlement');
  if (claimError) throw new Error('We could not find your purchase. Try again or ask for help.');
  const { data: entitlements, error } = await supabase
    .from('entitlements')
    .select('id,active,product_key')
    .eq('product_key', config.productKey)
    .eq('active', true)
    .order('activated_at', { ascending: false })
    .limit(1);
  if (error) throw error;
  const entitlement = entitlements?.[0];
  if (!entitlement) {
    showLocked(user.email);
    return;
  }
  if (isolateAccount(user)) return;
  metadata.entitlementUserId = user.id;
  metadata.entitlementVerifiedAt = new Date().toISOString();
  saveMetadata();
  await initializeSync();
}

// Keep each signed-in account's local data and revisions in its own slot.
function isolateAccount(user) {
  const previousOwner = metadata.ownerUserId || metadata.entitlementUserId;
  if (previousOwner && previousOwner !== user.id) {
    clearTimeout(syncTimer);
    localStorage.setItem('clearleaf_account_' + previousOwner, JSON.stringify({data:localStorage.getItem(DATA_KEY),metadata}));
    const saved = JSON.parse(localStorage.getItem('clearleaf_account_' + user.id) || 'null');
    if (saved?.data) localStorage.setItem(DATA_KEY,saved.data); else localStorage.removeItem(DATA_KEY);
    metadata=createSyncMetadata(saved?.metadata || {});
    metadata.ownerUserId=user.id;
    metadata.entitlementUserId=user.id;
    metadata.entitlementVerifiedAt=new Date().toISOString();
    saveMetadata();
    window.location.reload();
    return true;
  }
  const localData = readLocalData();
  if (!previousOwner && hasMeaningfulBudgetData(localData) && !metadata.ownerUserId) {
    showChoice({title:"Keep this browser's budget?",body:"This browser has a budget saved before accounts were added. If it's yours, keep it. Otherwise, use the budget saved to your account instead.",primary:'Keep this budget',onPrimary:async()=>{metadata.ownerUserId=user.id;metadata.cloudRevision=0;saveMetadata();await enterAuthenticatedApp(user);},secondary:"Use my account's budget",onSecondary:async()=>{
      localStorage.setItem('clearleaf_unclaimed_backup',localStorage.getItem(DATA_KEY));
      localStorage.removeItem(DATA_KEY);metadata=createSyncMetadata({ownerUserId:user.id});saveMetadata();window.location.reload();
    }});
    return true;
  }
  metadata.ownerUserId=user.id;
  if (!previousOwner) saveMetadata();
  return false;
}

function isOfflineAccessValid(user) {
  if (!user?.id || (metadata.ownerUserId && metadata.ownerUserId !== user.id) || metadata.entitlementUserId !== user.id) return false;
  const verifiedAt = Date.parse(metadata.entitlementVerifiedAt || '');
  if (!Number.isFinite(verifiedAt)) return false;
  const age = Date.now() - verifiedAt;
  return age >= 0 && age <= OFFLINE_ACCESS_MAX_AGE_MS;
}

function showOfflineVerificationRequired() {
  setGate(`
    <h1>Connect to the internet</h1>
    <p>Connect to the internet and sign in first. Then you can use the saved app offline.</p>
    <div class="clearleaf-cloud-actions">
      <button class="clearleaf-cloud-button" onclick="location.reload()">Try again</button>
      <a class="clearleaf-cloud-button secondary" href="https://clearleafdigital.com/support">Get support</a>
    </div>
  `);
}

function showSignIn(message = '', email = '') {
  setGate(`
    <h1>${isInstalledApp() ? 'Sign in to open your budget' : 'Open your budget'}</h1>
    ${isInstalledApp()
      ? '<p>The Home Screen app needs its own sign-in. Use the same email you used to buy Budget Tracker.</p><p>Your saved budget will be here when you sign in.</p>'
      : '<p>Enter the email you used to buy Budget Tracker.</p>'}
    ${message ? `<p class="clearleaf-cloud-error">${escapeHtml(message)}</p>` : ''}
    <form id="clearleafSignInForm">
      <label for="clearleafEmail">Email address</label>
      <input id="clearleafEmail" type="email" autocomplete="email" value="${escapeAttribute(email)}" required>
      <div class="clearleaf-cloud-actions">
        <button class="clearleaf-cloud-button" type="submit">Send my code</button>
        <a class="clearleaf-cloud-button secondary" href="${escapeAttribute(config.samcartCheckoutUrl)}">Buy Budget Tracker</a>
      </div>
    </form>
  `);
  document.getElementById('clearleafSignInForm').addEventListener('submit', sendSignInCode);
}

async function sendSignInCode(event) {
  event.preventDefault();
  const emailInput = document.getElementById('clearleafEmail');
  await requestSignInCode(emailInput.value.trim());
}

async function requestSignInCode(email) {
  const form = document.getElementById('clearleafSignInForm');
  const emailInput = document.getElementById('clearleafEmail');
  const button = form?.querySelector('button[type="submit"]');
  if (button?.disabled) return;
  if (emailInput) emailInput.disabled = true;
  if (button) {
    button.disabled = true;
    button.textContent = 'Sending…';
  }
  try {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        // Kept for the transition window: until the Supabase email template is
        // code-only, emails still carry a magic link for users on the old build.
        emailRedirectTo: `${window.location.origin}/`
      }
    });
    if (error) throw error;
    showCodeEntry(email);
  } catch (error) {
    showSignIn('We could not send the code. Check your email address and internet connection, then try again.', email);
  }
}

function showCodeEntry(email) {
  setGate(`
    <h1>Check your email</h1>
    <p>We sent an 8-digit code to <strong>${escapeHtml(email)}</strong>.</p>
    <form id="clearleafCodeForm">
      <label for="clearleafCode">Sign-in code</label>
      <input id="clearleafCode" type="text" inputmode="numeric" autocomplete="one-time-code"
             maxlength="10" pattern="[0-9]{6,10}" placeholder="Enter the code" required
             style="letter-spacing:8px;text-align:center;font-size:20px;">
      <p class="clearleaf-cloud-error" id="clearleafCodeError" hidden></p>
      <div class="clearleaf-cloud-actions">
        <button class="clearleaf-cloud-button" type="submit">Open my budget</button>
      </div>
      <div class="clearleaf-cloud-actions">
        <button class="clearleaf-cloud-button secondary" type="button" id="clearleafResend" disabled>Resend code (60s)</button>
        <button class="clearleaf-cloud-button secondary" type="button" id="clearleafUseAnother">Change email</button>
      </div>
    </form>
  `);
  const codeInput = document.getElementById('clearleafCode');
  document.getElementById('clearleafCodeForm').addEventListener('submit', event => verifyCode(event, email));
  document.getElementById('clearleafUseAnother').addEventListener('click', () => showSignIn());
  const resendButton = document.getElementById('clearleafResend');
  resendButton.disabled = true;
  let remaining = 60;
  resendButton.textContent = `Resend code (${remaining}s)`;
  const countdown = setInterval(() => {
    remaining -= 1;
    if (remaining <= 0) {
      clearInterval(countdown);
      resendButton.disabled = false;
      resendButton.textContent = 'Resend code';
    } else {
      resendButton.textContent = `Resend code (${remaining}s)`;
    }
  }, 1000);
  resendButton.addEventListener('click', () => {
    resendButton.disabled = true;
    clearInterval(countdown);
    requestSignInCode(email);
  });
  codeInput.focus();
}

async function verifyCode(event, email) {
  event.preventDefault();
  const form = event.currentTarget;
  const codeInput = document.getElementById('clearleafCode');
  const errorLine = document.getElementById('clearleafCodeError');
  const button = form.querySelector('button[type="submit"]');
  const code = codeInput.value.replace(/\D/g, '');
  errorLine.hidden = true;
  if (code.length < 6 || code.length > 10) {
    errorLine.textContent = 'Please enter the code from your email.';
    errorLine.hidden = false;
    return;
  }
  if (button.disabled) return;
  button.disabled = true;
  button.textContent = 'Signing in…';
  codeInput.disabled = true;
  try {
    const { data, error } = await supabase.auth.verifyOtp({ email, token: code, type: 'email' });
    if (error) throw error;
    await enterAuthenticatedApp(data.session?.user || data.user);
  } catch (error) {
    button.disabled = false;
    button.textContent = 'Sign in';
    codeInput.disabled = false;
    codeInput.value = '';
    codeInput.focus();
    errorLine.textContent = 'That code did not work. Use the newest code in your email and try again.';
    errorLine.hidden = false;
  }
}

function showLocked(email) {
  setGate(`
    <h1>Purchase not found</h1>
    <p>We could not find a Budget Tracker purchase for <strong>${escapeHtml(email || '')}</strong>.</p>
    <p>If you just bought it, wait a minute. Try the email shown on your receipt.</p>
    <div class="clearleaf-cloud-actions">
      <button class="clearleaf-cloud-button" id="clearleafRetry">Check again</button>
      <a class="clearleaf-cloud-button secondary" href="https://clearleafdigital.com/support">Get support</a>
      <button class="clearleaf-cloud-button secondary" id="clearleafSignOut">Change email</button>
    </div>
  `);
  document.getElementById('clearleafRetry').addEventListener('click', () => enterAuthenticatedApp(currentUser).catch(showFatal));
  document.getElementById('clearleafSignOut').addEventListener('click', async () => {
    await supabase.auth.signOut();
    currentUser = null;
    showSignIn();
  });
}

async function initializeSync() {
  if(metadata.ownerUserId!==currentUser?.id)throw new Error('Account mismatch');
  if(metadata.syncEnabled===false){revealAndStartSync();return;}
  if(localStorage.getItem('clearleaf_data_migrated')){metadata.pending=true;saveMetadata();localStorage.removeItem('clearleaf_data_migrated');}
  const localData = readLocalData();
  const cloudSnapshot = await fetchCloudSnapshot();
  const decision = decideInitialSync({ localData, cloudSnapshot, metadata, userId: currentUser.id });

  if(decision==='foreign-local')throw new Error('Account mismatch');
  if (decision === 'offer-upload') {
    showChoice({
      title: 'Back up this device to the cloud',
      body: 'This device has a budget, but your account has no copy yet. Save it to your account to use it on other devices.',
      primary: 'Back up and continue',
      onPrimary: async () => {
        metadata.syncEnabled = true;
        await saveSnapshot(localData, 0);
        revealAndStartSync();
      },
      secondary: 'Not now',
      onSecondary: () => {
        showChoice({
          title: 'Are you sure?',
          body: 'Your budget will stay only on this device. Other devices will not get these changes. You can turn on cloud sync in Settings later.',
          primary: 'Back up to cloud',
          onPrimary: async () => {
            metadata.syncEnabled = true;
            await saveSnapshot(localData, 0);
            revealAndStartSync();
          },
          secondary: 'Continue without sync',
          onSecondary: () => {
            metadata.syncEnabled = false;
            saveMetadata();
            revealAndStartSync();
          }
        });
      }
    });
    return;
  }

  if (decision === 'offer-download') {
    showChoice({
      title: 'We found your saved Budget Tracker',
      body: `This copy came from ${escapeHtml(cloudSnapshot.source_device_label || 'another device')} and was updated ${formatTime(cloudSnapshot.updated_at)}.`,
      primary: 'Use my saved data',
      onPrimary: () => importCloudSnapshot(cloudSnapshot),
      secondary: 'Start fresh on this device',
      onSecondary: () => {
        showChoice({
          title: 'Are you sure?',
          body: 'This device will start with a blank budget. Your account keeps its saved copy. New changes here will not reach your other devices. You can turn on cloud sync in Settings later.',
          primary: 'Use my saved data',
          onPrimary: () => importCloudSnapshot(cloudSnapshot),
          secondary: 'Continue without sync',
          onSecondary: () => {
            metadata.syncEnabled = false;
            saveMetadata();
            revealAndStartSync();
          }
        });
      }
    });
    return;
  }

  if (decision === 'resolve-existing-data') {
    showConflict(localData, cloudSnapshot);
    return;
  }

  if (cloudSnapshot) {
    metadata.cloudRevision = cloudSnapshot.revision;
    metadata.lastSyncedAt = cloudSnapshot.updated_at;
  }
  saveMetadata();
  revealAndStartSync();
}

function showConflict(localData, cloudSnapshot) {
  showChoice({
    title: 'Choose which copy to use',
    body: `This device and your account have different copies. The account copy came from ${escapeHtml(cloudSnapshot.source_device_label || 'another device')} and was updated ${formatTime(cloudSnapshot.updated_at)}. Choose one copy to keep. It will replace the other. The app will not join them.`,
    primary: 'Use cloud copy',
    onPrimary: () => importCloudSnapshot(cloudSnapshot),
    secondary: 'Keep this device',
    onSecondary: async () => {
      metadata.syncEnabled = true;
      await saveSnapshot(readLocalData(), cloudSnapshot.revision, true);
      revealAndStartSync();
    }
  });
}

function showChoice({ title, body, primary, onPrimary, secondary, onSecondary }) {
  setGate(`
    <h1>${escapeHtml(title)}</h1>
    <p>${body}</p>
    <div class="clearleaf-cloud-actions">
      <button class="clearleaf-cloud-button" id="clearleafPrimary">${escapeHtml(primary)}</button>
      <button class="clearleaf-cloud-button secondary" id="clearleafSecondary">${escapeHtml(secondary)}</button>
    </div>
  `);
  document.getElementById('clearleafPrimary').addEventListener('click', () => Promise.resolve(onPrimary()).catch(showFatal));
  document.getElementById('clearleafSecondary').addEventListener('click', () => Promise.resolve(onSecondary()).catch(showFatal));
}

async function fetchCloudSnapshot() {
  const { data, error } = await supabase
    .from('cloud_snapshots')
    .select('data,revision,source_device_id,source_device_label,updated_at')
    .eq('product_key', config.productKey)
    .maybeSingle();
  if (error) throw error;
  if (data && !validateBudgetData(data.data)) {
    console.warn('Cloud snapshot failed validation; ignoring invalid cloud copy.');
    return null;
  }
  return data;
}

async function saveSnapshot(data, expectedRevision = metadata.cloudRevision, force = false) {
  if (!currentUser || !metadata.syncEnabled || metadata.ownerUserId!==currentUser.id) return;
  if (!data) { console.warn('saveSnapshot called with null data; skipping.'); return; }
  if (syncInFlight) {
    resyncRequested = true;
    return;
  }
  if (!validateBudgetData(data)) throw new Error('We could not read this device’s budget. Saving to your account has stopped.');
  const savingUserId=currentUser.id;
  syncInFlight = true;
  setSyncStatus('Saving…');
  try {
    const { data: result, error } = await supabase.rpc('save_budget_snapshot', {
      p_product_key: config.productKey,
      p_data: data,
      p_device_id: metadata.deviceId,
      p_device_label: metadata.deviceLabel,
      p_expected_revision: expectedRevision,
      p_force: force
    });
    if (error) throw error;
    if(currentUser?.id!==savingUserId||metadata.ownerUserId!==savingUserId)return;
    const row = Array.isArray(result) ? result[0] : result;
    if (row?.conflict) {
      metadata.pending = true;
      saveMetadata();
      setSyncStatus('Needs attention');
      const cloud = await fetchCloudSnapshot();
      if (cloud) {
        showConflict(data, cloud);
      } else {
        // Cloud row disappeared (deleted by another device) - retry with revision 0
        metadata.cloudRevision=0;resyncRequested=true;
      }
      return;
    }
    metadata.cloudRevision = row?.revision != null ? Number(row.revision) : expectedRevision + 1;
    metadata.pending = false;
    metadata.lastSyncedAt = new Date().toISOString();
    saveMetadata();
    setSyncStatus('Saved online');
  } finally {
    syncInFlight = false;
    if (resyncRequested) {
      resyncRequested = false;
      scheduleSync();
    }
  }
}

function scheduleSync() {
  if (!metadata.syncEnabled || metadata.ownerUserId!==currentUser?.id) return;
  metadata.pending = true;
  saveMetadata();
  setSyncStatus(navigator.onLine ? 'Saving…' : 'Offline');
  clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    if (!navigator.onLine) return;
    saveSnapshot(readLocalData()).catch(error => {
      console.error('Cloud sync failed:', error);
      setSyncStatus(navigator.onLine ? 'Could not save online' : 'Offline');
    });
  }, 1200);
}

let syncListenersBound = false;

function revealAndStartSync({ reloadWhenOnline = false } = {}) {
  revealApp();
  ensureStatusNode();
  setSyncStatus(metadata.syncEnabled ? (navigator.onLine ? 'Saved online' : 'Offline') : 'Saved on this device');
  if (!syncListenersBound) {
    syncListenersBound = true;
    window.addEventListener('clearleaf:data-saved', scheduleSync);
    window.addEventListener('online', () => {
      if (reloadWhenOnline) {
        recheckOnlineAccess().catch(error=>{console.error(error);setSyncStatus('Could not save online');});
        return;
      }
      setSyncStatus('Back online');
      if (metadata.pending) scheduleSync();
    });
    window.addEventListener('offline', () => setSyncStatus('Offline'));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && metadata.pending && navigator.onLine) scheduleSync();
    });
  }
  if (metadata.pending && navigator.onLine) scheduleSync();
}

async function recheckOnlineAccess() {
  const userId=currentUser?.id;if(!userId||metadata.ownerUserId!==userId)return;
  const {error:claimError}=await supabase.rpc('claim_budget_tracker_entitlement');
  if(claimError)throw claimError;
  const {data:access,error}=await supabase.from('entitlements').select('id').eq('product_key',config.productKey).eq('active',true).limit(1);
  if(error)throw error;
  if(currentUser?.id!==userId)return;
  if(!access?.length){metadata.entitlementVerifiedAt=null;saveMetadata();showLocked(currentUser.email);return;}
  metadata.entitlementVerifiedAt=new Date().toISOString();saveMetadata();
  setSyncStatus(metadata.syncEnabled?'Back online':'Saved on this device');
  if(metadata.pending)scheduleSync();
}

function importCloudSnapshot(snapshot) {
  if (!validateBudgetData(snapshot.data)) throw new Error('We could not read the budget saved in your account.');
  localStorage.setItem(DATA_KEY, JSON.stringify(window.clearleafNormalizeBudgetData ? window.clearleafNormalizeBudgetData(snapshot.data) : snapshot.data));
  metadata.cloudRevision = Number(snapshot.revision) || 0;
  metadata.pending = false;
  metadata.syncEnabled = true;
  metadata.lastSyncedAt = snapshot.updated_at || new Date().toISOString();
  saveMetadata();
  window.location.reload();
}

function readLocalData() {
  try {
    const parsed = JSON.parse(localStorage.getItem(DATA_KEY) || 'null');
    return validateBudgetData(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function loadMetadata() {
  try {
    return createSyncMetadata(JSON.parse(localStorage.getItem(META_KEY) || '{}'));
  } catch {
    return createSyncMetadata();
  }
}

function saveMetadata() {
  localStorage.setItem(META_KEY, JSON.stringify(metadata));
}

function setGate(content) {
  let gate = document.getElementById('clearleafCloudGate');
  if (!gate) {
    gate = document.createElement('div');
    gate.id = 'clearleafCloudGate';
    gate.className = 'clearleaf-cloud-gate';
    document.body.appendChild(gate);
  }
  gate.innerHTML = `<section class="clearleaf-cloud-card">${content}</section>`;
  gate.style.visibility = 'visible';
}

function revealApp() {
  document.getElementById('clearleafCloudGate')?.remove();
  document.documentElement.classList.remove('clearleaf-cloud-pending');
  document.body.style.visibility = '';
}

let statusHideTimer = null;
let statusSeq = 0;
let statusInitialized = false;
const SYNC_CHECK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>';
const SYNC_WARN_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M17.5 19H9a7 7 0 1 1 6.7-9h1.8a4.5 4.5 0 0 1 0 9Z"/><path d="M3 3l18 18"/></svg>';

function ensureStatusNode() {
  if (statusNode) return;
  statusNode = document.createElement('button');
  statusNode.type = 'button';
  statusNode.className = 'clearleaf-sync-status';
  statusNode.title = 'Saved';
  statusNode.setAttribute('aria-live', 'polite');
  statusNode.innerHTML = SYNC_CHECK_SVG;
  statusNode.addEventListener('click', () => {
    if (statusNode.classList.contains('warn') && navigator.onLine && metadata.syncEnabled) saveSnapshot(readLocalData()).catch(() => setSyncStatus(navigator.onLine ? 'Could not save online' : 'Offline'));
  });
  document.body.appendChild(statusNode);
}

// Only a completed save receives a check. Pending and failed sync stay visible.
function setSyncStatus(text) {
  ensureStatusNode();
  const seq = ++statusSeq;
  if (statusHideTimer) clearTimeout(statusHideTimer);
  const success = text === 'Saved online' || text === 'Saved on this device';
  const quiet = text === 'Back online';
  const persistent = !success && !quiet;
  const first = !statusInitialized;
  statusInitialized = true;
  statusNode.classList.toggle('warn', persistent);
  statusNode.innerHTML = persistent ? SYNC_WARN_SVG + '<span>' + text + '</span>' : SYNC_CHECK_SVG;
  const label = text === 'Saving…' ? 'Saving online…' : text;
  statusNode.title = label + (persistent && text !== 'Saving…' ? '. Tap to try saving again.' : '');
  statusNode.setAttribute('aria-label', label);
  statusNode.disabled = !persistent || text === 'Saving…' || text === 'Offline';
  statusNode.tabIndex = statusNode.disabled ? -1 : 0;
  statusNode.classList.toggle('show', persistent || (success && !first));
  if (success && !first) {
    statusHideTimer = setTimeout(() => { if (seq === statusSeq) statusNode.classList.remove('show'); }, 2200);
  }
}

function showFatal(error) {
  console.error(error);
  setGate(`
    <h1>We could not open your account</h1>
    <p class="clearleaf-cloud-error">Check your internet connection and try again. If it still does not work, ask for help.</p>
    <div class="clearleaf-cloud-actions">
      <button class="clearleaf-cloud-button" onclick="location.reload()">Try again</button>
      <a class="clearleaf-cloud-button secondary" href="https://clearleafdigital.com/support">Get support</a>
    </div>
  `);
}

function formatTime(value) {
  if (!value) return 'recently';
  try { return new Date(value).toLocaleString(); } catch { return 'recently'; }
}

function escapeHtml(value) {
  const node = document.createElement('div');
  node.textContent = String(value ?? '');
  return node.innerHTML;
}

function escapeAttribute(value) {
  return String(value ?? '').replace(/["&<>']/g, character => ({ '"': '&quot;', '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;' })[character]);
}

window.addEventListener('clearleaf:update-available', event => {
  if (!confirm('A Budget Tracker update is ready. Reload now?')) return;
  event.detail.registration.waiting?.postMessage('SKIP_WAITING');
  window.location.reload();
});

window.enableCloudSync = async function() {
  if (!currentUser || metadata.ownerUserId!==currentUser.id) {
    alert('You need to be signed in to enable cloud sync.');
    return;
  }
  metadata.syncEnabled = true;
  saveMetadata();
  const localData = readLocalData();
  if (localData && Object.keys(localData).length > 0) {
    setSyncStatus('Saving…');
    await saveSnapshot(localData, 0);
  }
  if (!syncListenersBound) {
    revealAndStartSync();
  } else {
    setSyncStatus(navigator.onLine ? 'Saved online' : 'Offline');
  }
};

window.addEventListener('storage',event=>{
  if(event.key!==META_KEY)return;
  const next=loadMetadata();
  if(currentUser && next.ownerUserId!==currentUser.id){clearTimeout(syncTimer);currentUser=null;setGate('<h1>Account changed</h1><p>This browser switched accounts in another tab. Reload to open that account.</p><button class="clearleaf-cloud-button" onclick="location.reload()">Reload</button>');}
});
if(supabase)supabase.auth.onAuthStateChange((event,session)=>{
  if(currentUser && session?.user?.id!==currentUser.id){clearTimeout(syncTimer);currentUser=null;showSignIn('Your sign-in changed. Sign in again to open your budget.');}
});
